import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { analyticsService } from '../../services/analyticsService';
import { realtimeService } from '../../services/realtimeService';
import ExecutiveSummary from './components/ExecutiveSummary';
import ConversionFunnel from './components/ConversionFunnel';
import TimeSeriesChart from './components/TimeSeriesChart';
import RevenueForecasting from './components/RevenueForecasting';
import PipelineVelocity from './components/PipelineVelocity';
import PerformanceKPIs from './components/PerformanceKPIs';
import Toast from '../../components/ui/Toast';

const RealTimeAnalyticsDashboard = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [realtimeChannels, setRealtimeChannels] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    if (userProfile?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    loadAnalyticsData();
    setupRealtimeSubscriptions();

    return () => {
      realtimeService?.unsubscribeAll(realtimeChannels);
    };
  }, [user, userProfile, navigate, dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const data = await analyticsService?.getAnalyticsData(dateRange);
      setAnalyticsData(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    const channels = [];

    // Subscribe to consultation bookings
    const bookingsChannel = realtimeService?.subscribeToConsultationBookings(
      (newBooking) => {
        console.log('New booking detected:', newBooking);
        loadAnalyticsData();
        setToast({
          message: `New consultation: ${newBooking?.client_name}`,
          type: 'success'
        });
      },
      (updatedBooking) => {
        console.log('Booking updated:', updatedBooking);
        loadAnalyticsData();
        setToast({
          message: `Consultation status updated: ${updatedBooking?.status}`,
          type: 'info'
        });
      }
    );
    channels?.push(bookingsChannel);

    // Subscribe to conversion events
    const conversionChannel = realtimeService?.subscribeToConversionEvents(
      (newEvent) => {
        console.log('New conversion event:', newEvent);
        loadAnalyticsData();
      }
    );
    channels?.push(conversionChannel);

    // Subscribe to sales pipeline
    const pipelineChannel = realtimeService?.subscribeToSalesPipeline(
      (newPipeline) => {
        console.log('New pipeline entry:', newPipeline);
        loadAnalyticsData();
        setToast({
          message: `New deal in pipeline: ${newPipeline?.current_stage}`,
          type: 'success'
        });
      },
      (updatedPipeline) => {
        console.log('Pipeline updated:', updatedPipeline);
        loadAnalyticsData();
        setToast({
          message: `Deal stage changed: ${updatedPipeline?.current_stage}`,
          type: 'info'
        });
      }
    );
    channels?.push(pipelineChannel);

    // Subscribe to lead scores
    const leadScoresChannel = realtimeService?.subscribeToLeadScores(
      (newScore) => {
        console.log('New lead score:', newScore);
        loadAnalyticsData();
      },
      (updatedScore) => {
        console.log('Lead score updated:', updatedScore);
        loadAnalyticsData();
      }
    );
    channels?.push(leadScoresChannel);

    setRealtimeChannels(channels);
  };

  const handleRefresh = () => {
    loadAnalyticsData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading analytics dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
        <Header />
        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto" />
            <p className="mt-4 text-red-600">{error}</p>
            <Button onClick={handleRefresh} className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const conversionMetrics = analyticsService?.calculateConversionMetrics(
    analyticsData?.bookings,
    analyticsData?.pipelines
  );

  const conversionFunnel = analyticsService?.getConversionFunnel(
    analyticsData?.bookings,
    analyticsData?.pipelines
  );

  const timeSeriesData = analyticsService?.getTimeSeriesData(analyticsData?.bookings);

  const revenueForecasting = analyticsService?.calculateRevenueForecasting(
    analyticsData?.bookings,
    analyticsData?.pipelines
  );

  const pipelineVelocity = analyticsService?.calculatePipelineVelocity(analyticsData?.pipelines);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-surface to-background">
      <Helmet>
        <title>Real-Time Analytics Dashboard - Business Intelligence Center | Northstar Analytics</title>
        <meta name="description" content="Comprehensive business intelligence dashboard for monitoring consultation-to-deal conversion performance and revenue pipeline health" />
      </Helmet>
      <Header />

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="bg-white rounded-2xl shadow-elevation-2 p-8 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-foreground">
                    Real-Time Analytics Dashboard
                  </h1>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  Business Intelligence Center - Consultation-to-Deal Performance Metrics
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex gap-3">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Time</option>
                  <option value="this_month">This Month</option>
                  <option value="last_3_months">Last 3 Months</option>
                  <option value="last_6_months">Last 6 Months</option>
                  <option value="this_year">This Year</option>
                </select>
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Executive Summary */}
          <ExecutiveSummary metrics={conversionMetrics} />

          {/* Conversion Funnel & Time Series */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <ConversionFunnel data={conversionFunnel} />
            <TimeSeriesChart data={timeSeriesData} />
          </div>

          {/* Revenue Forecasting */}
          <RevenueForecasting data={revenueForecasting} />

          {/* Pipeline Velocity & Performance KPIs */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <PipelineVelocity data={pipelineVelocity} />
            <PerformanceKPIs 
              bookings={analyticsData?.bookings}
              pipelines={analyticsData?.pipelines}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeAnalyticsDashboard;